/**
 * buildQlrFile.ts
 *
 * Assembles QGIS Layer Definition (.qlr) XML. Every builder returns a complete
 * .qlr XML string; `downloadQlrFile()` triggers the browser download.
 *
 * Live (~8 h — the credential is embedded in the datasource and expires on its own):
 *   buildCmTableQlr(slug, layerName, datasource, style, geometryType)
 *     — datasource: the bare `?dl_token=` GeoJSON URL (no `/vsicurl/`).
 *   buildRemoteVectorQlr(name, datasource, style, geometryType, crs)
 *     — spatial dataset's signed GeoParquet, or GPKG + `|layername=layer`.
 *   buildRasterCogQlr(name, datasource, crs, pipeXml?)
 *     — spatial dataset's signed COG, optionally with a raster <pipe>.
 *
 * Style file (.qml) — applied by QGIS automatically when it sits beside a data
 * file of the same basename (what QGIS's own "Save as Default" writes):
 *   buildQmlStyle(innerXml), qmlFilenameFor(dataFilename), downloadQmlFile(...)
 *
 * `xyzvectortiles` / vector PMTiles are never a QGIS datasource in this app —
 * see the "Do not" list in the 9 Sep 2026 QGIS-export follow-on doc.
 *
 * See docs/developer-guides/QGIS_EXPORT_FRONTEND_CONTRACT.md §5.
 */
import type { Style } from "geostyler-style";
import { geostylerToQgisLabeling, geostylerToQgisRenderer, type QgisStyleOptions } from "./geostylerToQgis";
import { saveBlob } from "./browserDownload";

/**
 * Escapes a string for safe inclusion inside an XML attribute or text node.
 */
function xmlEsc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// ─── Coordinate reference system XML ──────────────────────────────────────────

/**
 * QGIS does not accept a lone `<authid>` as a usable
 * `QgsCoordinateReferenceSystem`. Reading a `.qlr`, it resolves the CRS from the
 * `<wkt>` / `<proj4>` payload and treats the rest as metadata — so a skinny
 * block loads the raster but leaves Layer Properties → General blank and raises
 * "CRS not defined or bad", even though the file itself is fine. (Confirmed with
 * `gdalinfo` on a served COG: Coordinate System is EPSG:3857 as expected.)
 *
 * A QGIS-authored `.qlr` carries wkt, proj4, srsid, srid, authid, description,
 * projectionacronym, ellipsoidacronym and geographicflag. We emit the same.
 *
 * Definitions are the canonical GDAL WKT1 / proj4 strings for each code. Only
 * the CRSs we actually emit are listed; anything else falls back to the old
 * authid-only block, which is no worse than before and better than inventing a
 * projection.
 */
interface CrsDefinition {
  wkt: string;
  proj4: string;
  /** QGIS-internal srs.db row id. Distinct from the EPSG code. */
  srsid: string;
  srid: string;
  description: string;
  projectionacronym: string;
  ellipsoidacronym: string;
  geographic: boolean;
}

const KNOWN_CRS: Record<string, CrsDefinition> = {
  "EPSG:3857": {
    wkt: 'PROJCS["WGS 84 / Pseudo-Mercator",GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]],PROJECTION["Mercator_1SP"],PARAMETER["central_meridian",0],PARAMETER["scale_factor",1],PARAMETER["false_easting",0],PARAMETER["false_northing",0],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","3857"]]',
    proj4: "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs",
    srsid: "3857",
    srid: "3857",
    description: "WGS 84 / Pseudo-Mercator",
    projectionacronym: "merc",
    ellipsoidacronym: "EPSG:7030",
    geographic: false
  },
  "EPSG:4326": {
    wkt: 'GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AXIS["Latitude",NORTH],AXIS["Longitude",EAST],AUTHORITY["EPSG","4326"]]',
    proj4: "+proj=longlat +datum=WGS84 +no_defs",
    srsid: "3452",
    srid: "4326",
    description: "WGS 84",
    projectionacronym: "longlat",
    ellipsoidacronym: "EPSG:7030",
    geographic: true
  },
  // The four entries below are common Australian originals (source_epsg on an
  // uploaded GeoTIFF/shapefile), added per the 9 Sep 2026 QGIS-export follow-on
  // work. wkt/proj4/authid/description/projectionacronym/ellipsoidacronym were
  // pulled from the EPSG registry and are authoritative. `srsid` is QGIS's own
  // internal srs.db row id (see the interface doc above) — unlike the two
  // entries above, this was NOT read out of an actual QGIS install (no access
  // to one here), so it's a best-effort placeholder (the EPSG code itself).
  // `wkt` + `authid` are what actually defines the CRS; `srsid` is auxiliary.
  // Confirm against a real QGIS session before relying on it (the existing
  // Phase-0 QGIS verification pass is the natural place) and correct if wrong.
  "EPSG:28350": {
    wkt: 'PROJCS["GDA94 / MGA zone 50",GEOGCS["GDA94",DATUM["Geocentric_Datum_of_Australia_1994",SPHEROID["GRS 1980",6378137,298.257222101],TOWGS84[0,0,0,0,0,0,0]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4283"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",117],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","28350"]]',
    proj4: "+proj=utm +zone=50 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    srsid: "28350",
    srid: "28350",
    description: "GDA94 / MGA zone 50",
    projectionacronym: "utm",
    ellipsoidacronym: "EPSG:7019",
    geographic: false
  },
  "EPSG:28351": {
    wkt: 'PROJCS["GDA94 / MGA zone 51",GEOGCS["GDA94",DATUM["Geocentric_Datum_of_Australia_1994",SPHEROID["GRS 1980",6378137,298.257222101],TOWGS84[0,0,0,0,0,0,0]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4283"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",123],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","28351"]]',
    proj4: "+proj=utm +zone=51 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    srsid: "28351",
    srid: "28351",
    description: "GDA94 / MGA zone 51",
    projectionacronym: "utm",
    ellipsoidacronym: "EPSG:7019",
    geographic: false
  },
  "EPSG:7844": {
    wkt: 'GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]]',
    proj4: "+proj=longlat +ellps=GRS80 +no_defs",
    srsid: "7844",
    srid: "7844",
    description: "GDA2020",
    projectionacronym: "longlat",
    ellipsoidacronym: "EPSG:7019",
    geographic: true
  },
  "EPSG:7855": {
    wkt: 'PROJCS["GDA2020 / MGA zone 55",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101],TOWGS84[0,0,0,0,0,0,0]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",147],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7855"]]',
    proj4: "+proj=utm +zone=55 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    srsid: "7855",
    srid: "7855",
    description: "GDA2020 / MGA zone 55",
    projectionacronym: "utm",
    ellipsoidacronym: "EPSG:7019",
    geographic: false
  }
};

/**
 * Backend `source_epsg` arrives as bare digits (`"4326"`), not an authid
 * (`"EPSG:4326"`) — `KNOWN_CRS` keys and QGIS's own `<authid>` convention both
 * expect the prefix, so a bare code silently misses the lookup and falls back
 * to the skinny authid-only block this file exists to avoid. Any other input
 * (already-prefixed, or an unrecognised scheme) passes through unchanged.
 */
function normalizeAuthid(authid: string): string {
  return /^\d+$/.test(authid) ? `EPSG:${authid}` : authid;
}

/**
 * Builds the `<spatialrefsys>` body for a `.qlr`.
 *
 * @param authid CRS of the *display* artifact, e.g. "EPSG:3857" — pass
 *   `display_crs` / `qgis.crs` from qgis-export. Never `source_epsg`: that is
 *   the CRS of the original upload, and using it would misplace the layer.
 * @param indent Leading whitespace so the emitted XML lines up with its parent.
 */
export function buildSpatialRefSysXml(authid: string, indent = "        "): string {
  const normalized = normalizeAuthid(authid ?? "");
  const safeAuthid = xmlEsc(normalized);
  const def = KNOWN_CRS[normalized.toUpperCase()];
  const pad = `${indent}  `;

  if (!def) {
    return `${indent}<spatialrefsys>\n${pad}<authid>${safeAuthid}</authid>\n${indent}</spatialrefsys>`;
  }

  return [
    `${indent}<spatialrefsys>`,
    `${pad}<wkt>${xmlEsc(def.wkt)}</wkt>`,
    `${pad}<proj4>${xmlEsc(def.proj4)}</proj4>`,
    `${pad}<srsid>${def.srsid}</srsid>`,
    `${pad}<srid>${def.srid}</srid>`,
    `${pad}<authid>${safeAuthid}</authid>`,
    `${pad}<description>${xmlEsc(def.description)}</description>`,
    `${pad}<projectionacronym>${def.projectionacronym}</projectionacronym>`,
    `${pad}<ellipsoidacronym>${def.ellipsoidacronym}</ellipsoidacronym>`,
    `${pad}<geographicflag>${def.geographic ? "true" : "false"}</geographicflag>`,
    `${indent}</spatialrefsys>`
  ].join("\n");
}

// ─── CM-table — vector GeoJSON ─────────────────────────────────────────────

/**
 * Builds a .qlr for a CM-table layer. QGIS loads GeoJSON from the tokenized
 * URL (no Authorization header needed) and applies native QGIS renderer XML.
 *
 * @param slug        CM-table slug — used as the layer id and filename base.
 * @param layerName   Human-readable name shown in QGIS Layers panel.
 * @param datasource  The `?dl_token=` GeoJSON URL from POST .../download-token/,
 *   bare. GeoJSON has no index, so a bare URL makes QGIS fetch the whole
 *   document in one GET; `/vsicurl/` would switch it to range reads, which the
 *   endpoint doesn't support, and fail above ~160 KiB.
 * @param style       Stored GeoStyler style. It is translated directly to QGIS XML.
 * @param geometryType CM-table geometry type.
 */
export function buildCmTableQlr(
  slug: string,
  layerName: string,
  datasource: string,
  style: Style | undefined,
  geometryType: QgisGeometryType,
  options: QgisStyleOptions = {}
): string {
  const safeId = xmlEsc(slug);
  const safeName = xmlEsc(layerName);
  const safeUrl = xmlEsc(datasource);

  const { renderer, labeling } = vectorStyleXml(style, geometryType, options);
  const geomCode = geometryTypeToQgisCode(geometryType);

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE qgis-layer-definition>
<qlr>
  <layer-tree-group name="" checked="Qt::Checked" expanded="1">
    <custom-properties/>
    <layer-tree-layer id="${safeId}" name="${safeName}"
      source="${safeUrl}" providerKey="ogr"
      checked="Qt::Checked" expanded="1">
      <customproperties/>
    </layer-tree-layer>
  </layer-tree-group>
  <maplayers>
    <maplayer type="vector" geometry="Unknown" autoRefreshEnabled="0" autoRefreshTime="0" labelsEnabled="${labeling ? 1 : 0}">
      <id>${safeId}</id>
      <datasource>${safeUrl}</datasource>
      <keywordList><value/></keywordList>
      <layername>${safeName}</layername>
      <srs>
${buildSpatialRefSysXml("EPSG:4326")}
      </srs>
      <provider encoding="UTF-8">ogr</provider>
      <vectorjoins/>
      <layerGeometryType>${geomCode}</layerGeometryType>
      ${renderer}${labeling}
      <customproperties/>
      <blendMode>0</blendMode>
      <featureBlendMode>0</featureBlendMode>
      <layerOpacity>1</layerOpacity>
    </maplayer>
  </maplayers>
</qlr>`;
}

// ─── Spatial dataset — live vector (signed GeoParquet/GPKG) ────────────────

/**
 * Geometry type as returned by the qgis-export endpoint.
 * Used to pick the correct SLD symbolizer in the renderer.
 */
export type QgisGeometryType = "point" | "line" | "polygon";

/**
 * Builds a .qlr for a spatial dataset's LIVE vector layer — the analysis
 * GeoParquet (or GPKG fallback), over HTTP, styled, via the `ogr` provider with
 * a written CRS: a remote GeoParquet/GPKG has no `.prj` beside it, so the CRS
 * is stated explicitly, same reasoning as the raster
 * COG builder (§6.4 — a bare `<authid>` alone triggers QGIS's "CRS not defined
 * or bad" warning on a `.qlr`, even though the file itself is fine).
 *
 * There is no vector-tile equivalent of this builder and there will not be one:
 * `xyzvectortiles` does not exist in QGIS 4.0.3, and vector tiles are never a
 * QGIS datasource here regardless of version — see the "Do not" list in the
 * 9 Sep 2026 QGIS-export follow-on doc.
 *
 * @param name          Human-readable layer name.
 * @param datasource    A signed `/vsicurl/` datasource from qgis-export —
 *   GeoParquet (single-layer, no `|layername=`) or GPKG + `"|layername=layer"`.
 * @param style         Stored GeoStyler style.
 * @param geometryType  From qgis-export's `geometry_type`.
 * @param crs           CRS of the analysis artifact — qgis-export's `qgis.crs`
 *   (EPSG:4326 for vectors).
 */
export function buildRemoteVectorQlr(
  name: string,
  datasource: string,
  style: Style | undefined,
  geometryType: QgisGeometryType,
  crs = "EPSG:4326",
  options: QgisStyleOptions = {}
): string {
  const safeId = xmlEsc(name.replace(/\s+/g, "_").toLowerCase());
  const safeName = xmlEsc(name);
  const safeUrl = xmlEsc(datasource);
  const { renderer, labeling } = vectorStyleXml(style, geometryType, options);
  const geomCode = geometryTypeToQgisCode(geometryType);

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE qgis-layer-definition>
<qlr>
  <layer-tree-group name="" checked="Qt::Checked" expanded="1">
    <custom-properties/>
    <layer-tree-layer id="${safeId}" name="${safeName}"
      source="${safeUrl}" providerKey="ogr"
      checked="Qt::Checked" expanded="1">
      <customproperties/>
    </layer-tree-layer>
  </layer-tree-group>
  <maplayers>
    <maplayer type="vector" autoRefreshEnabled="0" autoRefreshTime="0" labelsEnabled="${labeling ? 1 : 0}">
      <id>${safeId}</id>
      <datasource>${safeUrl}</datasource>
      <layername>${safeName}</layername>
      <srs>
${buildSpatialRefSysXml(crs)}
      </srs>
      <provider encoding="UTF-8">ogr</provider>
      <vectorjoins/>
      <layerGeometryType>${geomCode}</layerGeometryType>
      ${renderer}${labeling}
      <blendMode>0</blendMode>
      <layerOpacity>1</layerOpacity>
    </maplayer>
  </maplayers>
</qlr>`;
}

// Removed: `buildVectorPmtilesQlr` / `providerKey="xyzvectortiles"`.
// `xyzvectortiles` does not exist in QGIS 4.0.3 and vector tiles are never a
// QGIS datasource in this app, live or offline — replaced by `buildRemoteVectorQlr`
// above (GeoParquet/GPKG over `ogr`, per the 9 Sep 2026 QGIS-export follow-on doc).

// ─── Spatial dataset — live raster (signed COG) ─────────────────────────────

/**
 * Builds a .qlr for a raster COG spatial dataset.
 * QGIS opens the COG via GDAL's /vsicurl/ virtual file system — no local
 * download required.
 *
 * @param name            Human-readable layer name.
 * @param qgisDatasource  `qgis.datasource` (the signed `/vsicurl/` COG) or
 *   `qgis_styled.datasource` (a GDAL VRT document, used as-is).
 * @param crs             CRS of the converted display artifact from qgis-export.
 * @param pipeXml         Raster `<pipe>` from `geostylerToQgisRasterPipe`, or ""
 *   to leave QGIS's own renderer and filters untouched.
 */
export function buildRasterCogQlr(name: string, qgisDatasource: string, crs: string, pipeXml = ""): string {
  const safeId = xmlEsc(name.replace(/\s+/g, "_").toLowerCase());
  const safeName = xmlEsc(name);
  const safeDatasource = xmlEsc(qgisDatasource);

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE qgis-layer-definition>
<qlr>
  <layer-tree-group name="" checked="Qt::Checked" expanded="1">
    <custom-properties/>
    <layer-tree-layer id="${safeId}" name="${safeName}"
      source="${safeDatasource}" providerKey="gdal"
      checked="Qt::Checked" expanded="1">
      <customproperties/>
    </layer-tree-layer>
  </layer-tree-group>
  <maplayers>
    <maplayer type="raster" autoRefreshEnabled="0" autoRefreshTime="0">
      <id>${safeId}</id>
      <datasource>${safeDatasource}</datasource>
      <layername>${safeName}</layername>
      <srs>
${buildSpatialRefSysXml(crs)}
      </srs>
      <provider>gdal</provider>${pipeXml ? `\n      ${pipeXml}` : ""}
      <blendMode>0</blendMode>
      <layerOpacity>1</layerOpacity>
    </maplayer>
  </maplayers>
</qlr>`;
}

// ─── Style file (.qml) ────────────────────────────────────────────────────────

/** A QGIS style file (.qml) wrapping a vector `<renderer-v2>` (+ `<labeling>`) or a raster `<pipe>`. */
export function buildQmlStyle(innerXml: string, opts: { labelsEnabled?: boolean } = {}): string {
  const labels = opts.labelsEnabled ? ` labelsEnabled="1"` : "";
  return `<!DOCTYPE qgis PUBLIC 'http://mrcc.com/qgis.dtd' 'SYSTEM'>
<qgis version="3.34.0" styleCategories="Symbology|Labeling"${labels}>
  ${innerXml}
</qgis>
`;
}

/** A vector layer's style file: the same renderer and labels its `.qlr` carries. */
export function buildVectorQml(style: Style | undefined, geometryType: QgisGeometryType, options: QgisStyleOptions = {}): string {
  const { renderer, labeling } = vectorStyleXml(style, geometryType, options);
  // QGIS applies a same-named .qml to every sublayer of the file (a GPX file's
  // waypoints/routes/tracks, a mixed-geometry table's points/lines/polygons).
  // Declaring the geometry makes it refuse this one for the others, which then
  // keep QGIS's default look rather than drawing nothing.
  const geometry = `\n  <layerGeometryType>${geometryTypeToQgisCode(geometryType)}</layerGeometryType>`;
  return buildQmlStyle(renderer + labeling + geometry, { labelsEnabled: !!labeling });
}

/**
 * The style file QGIS applies automatically beside `dataFilename`: it drops a
 * trailing `.zip`, then one more extension, and adds `.qml`.
 * `Heritage.geojson` → `Heritage.qml`, `roads.zip` → `roads.qml`,
 * `parks.shp.zip` → `parks.qml`.
 */
export function qmlFilenameFor(dataFilename: string): string {
  return dataFilename.replace(/\.zip$/i, "").replace(/\.[^./\\]+$/, "") + ".qml";
}

// ─── Browser download helpers ─────────────────────────────────────────────────

/**
 * Triggers a browser download of the given .qlr XML string.
 * @param filename  Suggested filename (without extension).
 * @param qlrXml    Complete .qlr XML string.
 */
export function downloadQlrFile(filename: string, qlrXml: string): void {
  downloadXml(`${filename}.qlr`, qlrXml);
}

/**
 * Triggers a browser download of the given .qml XML string.
 * @param filename  Full filename, extension included — see `qmlFilenameFor`.
 */
export function downloadQmlFile(filename: string, qmlXml: string): void {
  downloadXml(filename, qmlXml);
}

function downloadXml(filename: string, xml: string): void {
  saveBlob(filename, new Blob([xml], { type: "application/xml" }));
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function vectorStyleXml(style: Style | undefined, geometryType: QgisGeometryType, options: QgisStyleOptions) {
  return { renderer: geostylerToQgisRenderer(style, geometryType, options), labeling: geostylerToQgisLabeling(style, geometryType) };
}

/**
 * Maps the qgis-export geometry_type to the QGIS internal layer geometry code.
 *   0 = Point, 1 = Line, 2 = Polygon
 */
function geometryTypeToQgisCode(gt: QgisGeometryType): number {
  switch (gt) {
    case "point":
      return 0;
    case "line":
      return 1;
    case "polygon":
      return 2;
  }
}
